import { useMutation } from '@truepill/tpos-react-router'
import { UserRoles, CopayStatus, RxFillRequestStatus, LaunchDarkly } from '@truepill/tpos-types'
import AuthLimited from 'components/AuthLimited'
import { ActionButtonContainer } from 'components/PageStructure'
import { CREATE_LOG, CREATE_NOTE, UPDATE_PRIOR_AUTHORIZATION } from 'gql'
import { useOrderLock, useCopayLock } from 'hooks'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import usePriorAuthorizationLock from 'hooks/usePriorAuthorizationLock'
import { useFlag } from 'providers/LaunchDarklyProvider'
import type { Fill, QueueItem, Patient, Prescription, PriorAuthorization } from 'types'
import { isOrder, getItemFill, isCopayRequest } from 'utils'
import { CancelPriorAuthorizationButton } from './actionButtons/CancelPriorAuthorizationButton'
import { ChangeDispensedActionButton } from './actionButtons/changeDispensedActionButton'
import { CreatePriorAuthorizationButton } from './actionButtons/CreatePriorAuthorizationButton'
import { EditPriorAuthorizationActionButton } from './actionButtons/EditPriorAuthorizationButton'
import { EligibilityCheckActionButton } from './actionButtons/eligibilityCheckActionButton'
import { FaxPriorAuthorizationButton } from './actionButtons/FaxPriorAuthorizationButton'
import { GetPriorAuthorizationInfoButton } from './actionButtons/GetPriorAuthorizationInfoButton'
import { RejectActionButton } from './actionButtons/rejectActionButton'
import { ReverseActiveCopayClaimsActionButton } from './actionButtons/reverseActiveCopayClaimsActionButton'
import { ReverseClaimsActionButton } from './actionButtons/reverseClaimsActionButton'
import { SendBackActionButton } from './actionButtons/sendBackActionButton'
import { StartPriorAuthorizationButton } from './actionButtons/StartPriorAuthorizationButton'
import { StatusCheckPriorAuthorizationButton } from './actionButtons/StatusCheckPriorAuthorizationButton'
import { SubmitClaimActionButton } from './actionButtons/submitClaimActionButton'
import { TriageOrResolveTriageActionButton } from './actionButtons/triageOrResolveTriageActionButton'

export interface OrderInsuranceInput {
  _id: string
  orderInsuranceOptionId?: string
  bin?: string
  pcn?: string
  group?: string
  cardholderId?: string
  name?: string
  helpdeskNumber?: string
  personCode?: string
  relationship?: string
  images?: {
    front: string
    back?: string
  }
  cardType?: string
  coreInsuranceToken?: string
}

export interface PatientInsuranceInput {
  cardholderId?: string
  bin: string
  name?: string
  designation?: string
  pcn?: string
  group?: string
  images?: {
    front: string
    back?: string
  }
}

type ActionButtonsProps = {
  fill?: Fill
  item?: QueueItem
  patient: Patient
  prescription: Prescription
  priorAuthorization?: PriorAuthorization
  isPriorAuthorization?: boolean
  isManualPriorAuth?: boolean
  hasRunClaim?: boolean
  setRunningClaim?: (val: boolean) => void
  disabled?: boolean
  isCopayInTriage?: boolean
  onlyAdminCanExecuteAction?: boolean
  disableStatusCheck?: boolean
}

// Action buttons need a Prescription object to function correctly
const WrappedActionButtons = (props: ActionButtonsProps): JSX.Element => {
  return (
    <AuthLimited
      roles={[UserRoles.Pharmacist, UserRoles.Technician, UserRoles.CustomerSupport, UserRoles.LeadCustomerSupport]}
    >
      <ActionButtons {...props} />
    </AuthLimited>
  )
}

const ActionButtons = ({
  fill,
  item,
  patient,
  prescription,
  priorAuthorization,
  isPriorAuthorization = false,
  isManualPriorAuth = false,
  setRunningClaim,
  disabled = false,
  isCopayInTriage = false,
  onlyAdminCanExecuteAction = true,
  disableStatusCheck = false,
}: ActionButtonsProps) => {
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()

  const [createLog] = useMutation(CREATE_LOG, {
    refetchQueries: ['getAllLogs'],
  })

  const [createNote] = useMutation(CREATE_NOTE, {
    refetchQueries: ['getAllLogs'],
  })

  const [updatePriorAuth] = useMutation(UPDATE_PRIOR_AUTHORIZATION, {
    onCompleted() {
      showSuccessToast(`Prior Authorization successfully updated`)
    },
    onError(err) {
      showErrorToast(`Failed to update Prior Authorization: ${err.message}`)
    },
  })

  const faxPaToPrescriberEnabled = useFlag(LaunchDarkly.FeatureFlags.PA_FAX_PRESCRIBER_BUTTON)
  const isOrderItem = item ? isOrder(item) : false
  const itemIdForLock = item?._id || undefined
  const { orderEditable } = useOrderLock(itemIdForLock, item && !isOrderItem)
  const { copayEditable } = useCopayLock(itemIdForLock, item && isOrderItem)
  const { priorAuthorizationEditable } = usePriorAuthorizationLock(priorAuthorization?._id, isPriorAuthorization)

  disabled =
    disabled ||
    (isOrderItem && !orderEditable) ||
    (!isOrderItem && !copayEditable) ||
    (isPriorAuthorization && !priorAuthorizationEditable)
  const isRejectedCopay =
    fill && item ? isCopayRequest(item) && getItemFill(item, fill._id)?.status === CopayStatus.Rejected : false
  const isRejectedOrder =
    fill && item ? isOrder(item) && getItemFill(item, fill._id)?.status === RxFillRequestStatus.Cancelled : false

  // All PA buttons should be visible, regardless of copay status JR-22235
  if (!isPriorAuthorization && isRejectedCopay && item && fill) {
    return <ReverseActiveCopayClaimsActionButton item={item} fill={fill} disabled={disabled} />
  }

  const isCopayInitiatedPA = !!priorAuthorization?.initiatedBy?.copayRequestId

  return (
    <ActionButtonContainer>
      {/** Automated Prior Athorization Buttons Below This Line */}
      {/** Todo: Move Prior Authorization Buttons to its own component */}
      {item && fill && (
        <CreatePriorAuthorizationButton
          disabled={disabled}
          priorAuthorization={priorAuthorization}
          isPriorAuthorization={isPriorAuthorization}
          item={item}
          fill={fill}
          prescription={prescription}
        />
      )}
      {!!priorAuthorization && (
        <StatusCheckPriorAuthorizationButton
          disabled={disableStatusCheck || disabled}
          onClick={(message: string) => {
            createNote({
              variables: {
                isExternal: false,
                message,
                priorAuthorizationId: priorAuthorization._id,
                prescriptionId: prescription._id,
                tags: ['Adjudication'],
              },
            })
            updatePriorAuth({
              variables: {
                priorAuthorizationId: priorAuthorization._id,
                data: {
                  lastAttemptAt: new Date(),
                },
              },
            })
          }}
        />
      )}
      <StartPriorAuthorizationButton
        disabled={disabled}
        priorAuthorization={priorAuthorization}
        isPriorAuthorization={isPriorAuthorization}
        clickCallback={priorAuthorizationId => {
          createLog({
            variables: {
              priorAuthorizationId,
              event: 'priorAuth',
              message: 'Started Prior Authorization',
            },
          })
        }}
      />

      {!!isPriorAuthorization && priorAuthorization && (
        <FaxPriorAuthorizationButton
          enabled={faxPaToPrescriberEnabled}
          prescription={prescription}
          disabled={disabled}
          priorAuthorization={priorAuthorization}
          onSubmit={() => {
            createLog({
              variables: {
                priorAuthorizationId: priorAuthorization._id,
                event: 'priorAuth',
                message: 'Started Prior Authorization',
              },
            })
          }}
        />
      )}

      <GetPriorAuthorizationInfoButton
        disabled={disabled}
        priorAuthorization={priorAuthorization}
        isPriorAuthorization={isPriorAuthorization}
        clickCallback={priorAuthorizationId => {
          createLog({
            variables: {
              priorAuthorizationId,
              event: 'priorAuth',
              message: 'Viewed Prior Authorization Info',
            },
          })
        }}
      />

      <CancelPriorAuthorizationButton
        disabled={disabled}
        priorAuthorization={priorAuthorization}
        isPriorAuthorization={isPriorAuthorization}
        clickCallback={priorAuthorizationId => {
          createLog({
            variables: {
              priorAuthorizationId,
              event: 'priorAuth',
              message: 'Cancelled Prior Authorization',
            },
          })
        }}
      />

      <EditPriorAuthorizationActionButton
        disabled={disabled}
        priorAuthorization={priorAuthorization}
        isPriorAuthorization={isPriorAuthorization}
        clickCallback={priorAuthorizationId => {
          createLog({
            variables: {
              priorAuthorizationId,
              event: 'priorAuth',
              message: 'Edited Prior Authorization',
            },
          })
        }}
      />

      {/* Only show these buttons if PA was initiated from a copay, or if not on the PA page */}
      {fill && item && (isCopayInitiatedPA || !isPriorAuthorization) && (
        <RejectActionButton
          disabled={disabled}
          fill={fill}
          item={item}
          isPriorAuthorization={isPriorAuthorization}
          isRejectedCopay={isRejectedCopay}
          isRejectedOrder={isRejectedOrder}
          priorAuthorization={priorAuthorization}
        />
      )}

      {fill && item && (
        <TriageOrResolveTriageActionButton
          item={item}
          fill={fill}
          isCopayInTriage={isCopayInTriage}
          isPriorAuthorization={isPriorAuthorization}
          isRejectedOrder={isRejectedOrder}
        />
      )}

      {/* // TODO -Send back to PENDING for copays has been created in sendBackToPending mutation*/}
      {!isPriorAuthorization && fill && item && (
        <SendBackActionButton item={item} fill={fill} isRejectedOrder={isRejectedOrder} />
      )}

      {item && fill && (
        <EligibilityCheckActionButton
          disabled={disabled}
          item={item}
          fill={fill}
          patient={patient}
          isPriorAuthorization={isPriorAuthorization}
          isManualPriorAuth={isManualPriorAuth}
        />
      )}

      {item && fill && (
        <ChangeDispensedActionButton
          item={item}
          fill={fill}
          prescription={prescription}
          isPriorAuthorization={isPriorAuthorization}
        />
      )}

      {/* Only show these buttons if PA was initiated from a copay, or if not on the PA page */}
      {item && fill && (isCopayInitiatedPA || !isPriorAuthorization) && (
        <ReverseClaimsActionButton
          item={item}
          fill={fill}
          isPriorAuthorization={isPriorAuthorization}
          isManualPriorAuth={isManualPriorAuth}
          disabled={disabled}
        />
      )}

      {fill && item && (isCopayInitiatedPA || !isPriorAuthorization) && (
        <SubmitClaimActionButton
          disabled={disabled}
          fill={fill}
          isPriorAuthorization={isPriorAuthorization}
          isRejectedCopay={isRejectedCopay}
          isRejectedOrder={isRejectedOrder}
          item={item}
          priorAuthorization={priorAuthorization}
          setRunningClaim={setRunningClaim}
        />
      )}
    </ActionButtonContainer>
  )
}

export default WrappedActionButtons
